import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux';

import _ from 'lodash';
import cx from "classnames";
import { Row, Col, Form, InputGroup } from 'react-bootstrap';

import { Loader } from 'components/ApplicationLoader';
import shortid from 'shortid';
import { AssetCard, GroupCardRender, AssetCardRender, StreamCard } from './components';
import {
  fetchGroupRequest,
  fetchHubStreamsRequest,
  fetchStreamRequest,
  invalidateGroup,
  invalidateStream,
  invalidateStreamAssetsSearch,
  runPaperfliteSearch,
  searchStreamAssetsRequest,
  searchHubStreamsRequest,
  searchStreamGroupsRequest,
  uploadStreamAsset
} from 'actions/paperflite';
import { AssetUploaderFooter } from '../allContents/components';
import { deletePaperfliteAsset } from 'actions/content';
import InfiniteScroll from 'components/InfiniteScroll';
import TruncatedTextWithTooltip from 'components/TruncatedTextWithTooltip';

import styles from './paperfliteAsset.module.scss';

export const PaperfliteAssetManager = ({ handleModalClose, handleSave, container }) => {
  const dispatch = useDispatch();

  const [state, setState] = useState({
    selectedStream: null,
    groupPath: [],
    currentGroup: null
  })

  const [searchInput, setSearchInput] = useState('');
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [searchFilter, setSearchFilter] = useState('assets');
  const isSearchActive = _.trim(activeSearchQuery).length > 0;

  const resetSearch = () => {
    setSearchInput('');
    setActiveSearchQuery('');
    setSearchFilter('assets');
    dispatch(invalidateStreamAssetsSearch());
  }

  const searchScopeKey = _.get(state, 'selectedStream.id') || 'hub';

  const runStreamSearch = (query) => {
    const trimmed = _.trim(query);
    const streamId = _.get(state, 'selectedStream.id');

    if (!trimmed) {
      resetSearch();
      return;
    }

    setActiveSearchQuery(trimmed);
    setSearchFilter('assets');
    dispatch(runPaperfliteSearch({
      streamId: streamId,
      query: trimmed,
      limit: 24,
      searchWithinHub: !streamId,
    }));
  }

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    if (!_.trim(value)) {
      resetSearch();
    }
  }

  useEffect(() => {
    dispatch(fetchHubStreamsRequest())

    return () => {
    }
  }, [])

  const uploadedAssets = useSelector((state) =>
    (state.content.uploads || []).filter(
      (asset) => _.get(asset, 'asset.source') === 'paperflite'
    )
  );

  const handleOnClick = (data) => {
    const entityType = _.get(data, 'entityType', null);

    switch (entityType) {
      case 'stream':
        const streamRequest = {
          id: _.get(data, 'id'),
          limit:20,
          sort: 'DESC',
          type: 'name'
        }

        resetSearch();
        setState((state) => {
          return {
            ...state,
            selectedStream: data
          }
        })
        dispatch(fetchStreamRequest(streamRequest, 0))
        return;

      case 'group':
        setState((state) => {
          return {
            ...state,
            groupPath: [...state.groupPath, data],
            currentGroup: data
          }
        })
        dispatch(fetchGroupRequest(data.id))
        return;

      case 'asset':
        const existingAssset = _.some(uploadedAssets, (assetContent) => _.get(assetContent, 'asset.id') === data.id); // Returns true

        if (!existingAssset) {
          const assetRequest = {
            id: shortid(),
            entityType: 'asset',
            source: 'PAPERFLITE',
            asset: {
              ...data,
              source: 'paperflite'
            }
          }
          dispatch(uploadStreamAsset(assetRequest));
        }
        return;

      default:
        return;
    }
  }

  const navigateBack = (data) => {
    const entityType = _.get(data, 'entityType', null);

    switch (entityType) {
      case 'stream':
        setState((state) => {
          return {
            ...state,
            groupPath: [],
            currentGroup: null
          }
        })

        dispatch(invalidateGroup())
        return;

      case 'group':
        const newPath = (state.groupPath).slice(0, data.index + 1)

        setState((state) => {
          return {
            ...state,
            groupPath: newPath,
            currentGroup: data
          }
        })

        return;

      default:
        resetSearch();
        setState((state) => {
          return {
            ...state,
            selectedStream: null,
            groupPath: [],
            currentGroup: null
          }
        })
        dispatch(invalidateStream())
        return;
    }
  }

  const handleDelete = (id) => {
    if (id) {
      dispatch(deletePaperfliteAsset(id));
    }
  }


  return (
    <div className={styles.wrapperConatiner}>
      <Row className={styles.layoutRow}>
        <Col className={styles.assetsLayout} xs={8}>
          <div className={styles.headerRow}>
            <HeaderBreadCrumb
              stream={state.selectedStream}
              groupPath={state.groupPath}
              navigateBack={navigateBack}
              container={container}
            />
            <StreamSearchBar
              placeholder={state.selectedStream
                ? `Search in ${_.get(state, 'selectedStream.name', '')}`
                : 'Search'}
              searchInput={searchInput}
              onSearchInputChange={handleSearchInputChange}
              onSearch={() => runStreamSearch(searchInput)}
            />
          </div>
          {!state.selectedStream && !isSearchActive &&
            <StreamsListRenderView
              handleOnClick={handleOnClick}
              container={container}
            />
          }
          {isSearchActive &&
            <>
              <SearchFilterTabs
                isHub={!state.selectedStream}
                searchFilter={searchFilter}
                onFilterChange={setSearchFilter}
                searchScopeKey={searchScopeKey}
              />
              <StreamSearchResultsView
                searchScopeKey={searchScopeKey}
                streamId={_.get(state, 'selectedStream.id')}
                activeSearchQuery={activeSearchQuery}
                searchFilter={searchFilter}
                handleOnClick={handleOnClick}
                container={container}
              />
            </>
          }
          {state.selectedStream && !isSearchActive &&
            <>
              {!state.currentGroup &&
                <StreamGroupRenderView
                  mappings={state.selectedStream}
                  type='stream'
                  handleOnClick={handleOnClick}
                  container={container}
                />
              }
              {state.currentGroup &&
                <StreamGroupRenderView
                  mappings={state.currentGroup}
                  type='group'
                  handleOnClick={handleOnClick}
                  container={container}
                />
              }
            </>
          }
        </Col>
        <Col className={styles.selectionList} xs={4}>
          <div className={styles.header}>
            <span className={styles.assetType}>Selected:</span>
            <span className={styles.folderPath}>{_.size(uploadedAssets)} Assets</span>
          </div>
          <div className={styles.selectedAssetView}>
            {_.map(uploadedAssets, (asset) => {
              return (
                <AssetCard
                  key={_.get(asset, 'id', null)}
                  className={styles.assetCard}
                  id={_.get(asset, 'id', null)}
                  assetMapping={asset}
                  handleDelete={handleDelete}
                  container={container}
                  onClick={() => { console.log('') }}
                />
              )
            })
            }
          </div>
          <AssetUploaderFooter
            className={styles.footer}
            handleSave={handleSave}
            handleClose={handleModalClose}
          />
        </Col>
      </Row>
    </div>
  )
}


const StreamSearchBar = ({ placeholder, searchInput, onSearchInputChange, onSearch }) => {
  return (
    <div className={styles.searchBar}>
      <InputGroup className={styles.searchBox}>
        <Form.Control
          className={styles.searchInput}
          placeholder={placeholder}
          aria-label={placeholder}
          value={searchInput}
          onChange={onSearchInputChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onSearch();
            }
          }}
        />
        <InputGroup.Text className={styles.searchIcon} onClick={onSearch}>
          <i className="cleverstory-search" />
        </InputGroup.Text>
      </InputGroup>
    </div>
  )
}

const SearchFilterTabs = ({ isHub, searchFilter, onFilterChange, searchScopeKey }) => {
  const searchState = useSelector(state => _.get(state, `paperflite.search.${searchScopeKey}`, {})) || {};
  const assetCount = _.get(searchState, 'assets.totalRecordsCount', 0);
  const streamCount = _.get(searchState, 'streams.totalRecordsCount', 0);
  const groupCount = _.get(searchState, 'groups.totalRecordsCount', 0);

  return (
    <div className={styles.searchFilters}>
      <span
        className={cx(styles.filterTab, { [styles.activePath]: searchFilter === 'assets' })}
        onClick={() => onFilterChange('assets')}
      >
        Assets ({assetCount})
      </span>
      {isHub &&
        <span
          className={cx(styles.filterTab, { [styles.activePath]: searchFilter === 'streams' })}
          onClick={() => onFilterChange('streams')}
        >
          Streams ({streamCount})
        </span>
      }
      {!isHub &&
        <span
          className={cx(styles.filterTab, { [styles.activePath]: searchFilter === 'groups' })}
          onClick={() => onFilterChange('groups')}
        >
          Groups ({groupCount})
        </span>
      }
    </div>
  )
}

const StreamSearchResultsView = ({ searchScopeKey, streamId, activeSearchQuery, searchFilter, handleOnClick, container }) => {
  const dispatch = useDispatch();
  const isHub = !streamId;

  const searchState = useSelector(state => _.get(state, `paperflite.search.${searchScopeKey}`, {})) || {};
  const assets = _.get(searchState, 'assets', {});
  const streams = _.get(searchState, 'streams', {});
  const groups = _.get(searchState, 'groups', {});
  const storedQuery = _.get(searchState, 'query', '');

  const activeSlice = searchFilter === 'assets'
    ? assets
    : (isHub ? streams : groups);

  const loading = searchFilter === 'assets'
    ? _.get(searchState, 'loadingAssets', false)
    : (isHub ? _.get(searchState, 'loadingStreams', false) : _.get(searchState, 'loadingGroups', false));

  const records = _.get(activeSlice, 'records', []) || [];
  const currentPage = _.get(activeSlice, 'currentPage', 0);
  const totalPages = _.get(activeSlice, 'totalPages', 0);
  const hasMoreItems = currentPage < totalPages - 1;
  const showEmptyResults = !loading && storedQuery === activeSearchQuery && _.size(records) === 0;

  const loadMoreSearchResults = () => {
    const nextPage = currentPage + 1;
    if (nextPage >= totalPages) {
      return;
    }

    if (searchFilter === 'assets') {
      dispatch(searchStreamAssetsRequest({
        streamId: streamId,
        query: activeSearchQuery,
        limit: 24,
        searchWithinHub: !streamId,
      }, nextPage));
      return;
    }

    if (isHub) {
      dispatch(searchHubStreamsRequest({
        query: activeSearchQuery,
        limit: 24,
        searchScopeKey: searchScopeKey,
      }, nextPage));
      return;
    }

    dispatch(searchStreamGroupsRequest({
      streamId: streamId,
      query: activeSearchQuery,
      limit: 24,
    }, nextPage));
  }

  const handleSelectSearchAsset = (asset) => {
    handleOnClick({
      ...asset,
      entityType: 'asset',
    });
  }

  const handleSelectSearchStream = (stream) => {
    handleOnClick({
      ...stream,
      entityType: 'stream',
    });
  }

  const handleSelectSearchGroup = (group) => {
    handleOnClick({
      ...group,
      entityType: 'group',
    });
  }

  return (
    <div className={cx(styles.streamGroupMapContainer, styles.withSearchBar, { [styles.loadingContainer]: (loading && _.size(records) === 0) })}>
      {loading && _.size(records) === 0 &&
        <div className={styles.loadingAnimationContainer}>
          <Loader className={styles.loadingAnimation} title="Searching..." />
        </div>
      }
      {showEmptyResults &&
        renderEmptySearchMessage(activeSearchQuery)
      }
      {_.size(records) > 0 &&
        <InfiniteScroll
          className={cx(styles.scrollable, styles.withSearchBar)}
          loading={loading}
          hasMore={hasMoreItems}
          loadMore={loadMoreSearchResults}
        >
          {searchFilter === 'assets' && _.map(records, (asset, index) => (
            <AssetCardRender
              key={_.get(asset, 'id', index)}
              asset={asset}
              onSelect={handleSelectSearchAsset}
              container={container}
            />
          ))}
          {searchFilter === 'streams' && _.map(records, (stream, index) => (
            <StreamCard
              key={_.get(stream, 'id', index)}
              stream={stream}
              onSelect={handleSelectSearchStream}
              container={container}
            />
          ))}
          {searchFilter === 'groups' && _.map(records, (group, index) => (
            <GroupCardRender
              key={_.get(group, 'id', index)}
              group={group}
              onSelect={handleSelectSearchGroup}
            />
          ))}
        </InfiniteScroll>
      }
    </div>
  )
}

const StreamsListRenderView = ({ handleOnClick, container }) => {

  const streams = useSelector(state => _.get(state, 'paperflite.items', [])) || [];
  const loading = useSelector(state => _.get(state, 'paperflite.loading', false));

  return (
    <div className={cx(styles.streamsMapContainer, { [styles.loadingContainer]: (_.size(streams) == 0 || loading) })}>
      {loading &&
        <div className={styles.loadingAnimationContainer}>
          <Loader className={styles.loadingAnimation} title="Loading..." />
        </div>
      }
      {(!loading && _.size(streams) == 0) &&
        renderEmptyMessage('stream')
      }
      {(_.size(streams) > 0 && !loading) &&
        _.map(streams, (stream, index) => {
          return (
            <StreamCard
              key={index}
              onSelect={handleOnClick}
              stream={stream}
              container={container}
            />
          )
        })
      }
    </div>
  )
}

const StreamGroupRenderView = ({ mappings, type, handleOnClick, container }) => {
  const dispatch = useDispatch();
  const id = _.get(mappings, 'id');
  const childrens = useSelector(state => _.get(state, `paperflite.${type}.${id}.item.children`, [])) || [];
  const loading = useSelector(state => _.get(state, `paperflite.${type}.${id}.loading`, false));
  const currentPage = useSelector(state => _.get(state, `paperflite.stream.${id}.item.currentPage`, {}));
  const totalPages = useSelector(state => _.get(state, `paperflite.stream.${id}.item.totalPages`, {}));
  const hasMoreItems = currentPage < totalPages - 1;

  const loadMoreAssets = () => {
    const nextPage = currentPage + 1;
    if (nextPage >= totalPages) {
      return;
    }
    handleFetchAssets(nextPage);
  }

  const handleFetchAssets = (page) => {
    var streamRequest = {
      id: id,
      limit: 20,
      sort: 'DESC',
      type: 'name'
    }

    dispatch(fetchStreamRequest(streamRequest, page));
  }

  return (
    <div className={cx(styles.streamGroupMapContainer, styles.withSearchBar, { [styles.loadingContainer]: (_.size(childrens) == 0 || loading) })}>
      {loading &&
        <div className={styles.loadingAnimationContainer}>
          <Loader className={styles.loadingAnimation} title="Loading..." />
        </div>
      }
      {(!loading && _.size(childrens) == 0) &&
        renderEmptyMessage('group')
      }
      {(_.size(childrens) > 0 && !loading) &&
      <InfiniteScroll
        className={cx(styles.scrollable, styles.withSearchBar)}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={() => {
          loadMoreAssets();
        }}
      >
        {
          _.map(childrens, (children, index) => {
            if (_.get(children, 'entityType') === 'asset') {
              return (
                <AssetCardRender
                  key={index}
                  asset={children}
                  onSelect={handleOnClick}
                  container={container}
                />
              )
            }
  
            return (
              <GroupCardRender
                key={index}
                onSelect={handleOnClick}
                group={children}
              />
            )
          })
        }
      </InfiniteScroll>
      }
    </div>
  )
}

const HeaderBreadCrumb = ({ stream, groupPath, navigateBack, container }) => {
  return (
    <div className={styles.header}>
      <span className={styles.assetType}>Paperflite:</span>
      <span onClick={() => { navigateBack() }} className={cx(styles.streamName, { [styles.activePath]: (!stream && _.size(groupPath) == 0) })}>Streams</span>
      {stream &&
        <span className={cx(styles.streamPath, { [styles.activePath]: (stream && _.size(groupPath) == 0) })}>
          <i className={cx(styles.backIcon, 'cleverstory-drop-down-right')} />
          <TruncatedTextWithTooltip 
            content={_.get(stream, 'name')} 
            maxWidth={200} 
            placement='bottom'
            onClick={() => {navigateBack(stream)}}
            container={container}
          />
        </span>
      }
      {(_.size(groupPath) > 0) &&
        _.map(groupPath, (path, index) => {
          const groupData = {
            ...path,
            index
          }
          return (
            <span key={index} className={cx(styles.folderPath, { [styles.activePath]: _.size(groupPath) == (index + 1) })}>
              <i className={cx(styles.backIcon, 'cleverstory-drop-down-right')} />
              <TruncatedTextWithTooltip 
                content={path.name} 
                maxWidth={200} 
                placement='bottom'
                onClick={() => {navigateBack(groupData)}}
                container={container}
              />
            </span>
          )
        })
      }
    </div>
  )
}

const renderEmptyMessage = (content) => {
  return (
    <div className={styles.emptyMessageContainer}>
      <div className={styles.emptyMessage}>
        <span>Ginger. Anybody in?<br /></span>
        <span>{`There is no content in this ${content} yet.`}</span>
      </div>
    </div>
  )
}

const renderEmptySearchMessage = (query) => {
  return (
    <div className={styles.emptyMessageContainer}>
      <div className={styles.emptyMessage}>
        <span>{`We couldn't find anything for "${query}".`}</span>
        <span>Try to refine your search.</span>
      </div>
    </div>
  )
}
